/**
 * `/gallery/marks` exists so a browser can measure every team abbreviation
 * mark (`ScoreRow`'s inline mark, `TeamMark` at both sizes, `FanTeamPlate`) at
 * two, three and four characters (ISSUE-541). It is signed in and linked from
 * no navigation, following the `/gallery` precedent.
 *
 * The file is named `gallery_.marks` so this route stays out of `/gallery`'s
 * tree: `/gallery` renders no Outlet, so a nested route would never paint.
 * Same trap `scores_.player.tsx` records in its own header comment.
 *
 * The large size is the team profile header mark (ISSUE-182).
 *
 * Since ISSUE-902 the page also draws the image branch, from an inlined sample
 * URL, because nothing else anywhere renders it until an operator seeds artwork
 * in production: this is where the drawn mark can be eyeballed at 390px and at
 * desktop before it reaches a single fan.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { FanTeamPlate } from "../features/fanProfile/FanTeamPlate";
import { TeamMark } from "../features/gameDetail/TeamMark";
import { ScoreRow } from "../features/scores/ScoreRow";
import type { typeScoresEventInput } from "../features/scores/scoreRowModel";

/**
 * A stand-in for a resolved media-api thumbnail, inlined so this page draws the
 * image branch with no service running and no artwork seeded. Deliberately a
 * wide, off-square shape: the mark box is fitted to text, so a real logo has to
 * letterbox inside it rather than stretch the plate (ISSUE-902).
 */
const SAMPLE_MARK_URL =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 48">' +
      '<rect width="96" height="48" rx="6" fill="#1f6feb"/>' +
      '<circle cx="24" cy="24" r="13" fill="#ffd166"/>' +
      '<rect x="46" y="14" width="38" height="20" rx="4" fill="#ffffff"/>' +
      "</svg>",
  );

const MARK_EVENTS: readonly typeScoresEventInput[] = [
  {
    eventId: "mark-1",
    status: "final",
    statusLabel: "Final",
    isLive: false,
    winnerTeamId: null,
    isTie: false,
    away: { teamId: "t-kc", name: "Chiefs", abbreviation: "KC", score: 24 },
    home: { teamId: "t-bal", name: "Ravens", abbreviation: "BAL", score: 20 },
  },
  {
    eventId: "mark-2",
    status: "final",
    statusLabel: "Final",
    isLive: false,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-wsh",
      name: "Commanders",
      abbreviation: "WSH",
      score: 24,
    },
    home: {
      teamId: "t-www",
      name: "Wide United",
      abbreviation: "WWW",
      score: 20,
    },
  },
  {
    eventId: "mark-3",
    status: "final",
    statusLabel: "Final",
    isLive: false,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-mmmm",
      name: "Wide Club",
      abbreviation: "MMMM",
      score: 24,
    },
    home: { teamId: "t-phil", name: "Union", abbreviation: "PHIL", score: 20 },
  },
  {
    eventId: "mark-4",
    status: "final",
    statusLabel: "Final",
    isLive: false,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-logo-away",
      name: "Drawn Mark",
      abbreviation: "DRW",
      score: 24,
      logoUrl: SAMPLE_MARK_URL,
    },
    home: {
      teamId: "t-logo-home",
      name: "Lettered Mark",
      abbreviation: "LTR",
      score: 20,
    },
  },
];

const MARK_ABBREVIATIONS = ["KC", "BAL", "WSH", "WWW", "MMMM", "PHIL"] as const;

const MARK_TEAM_IDS = [
  "mark-team-kc",
  "mark-team-wsh",
  "mark-team-mmmm",
] as const;

const MarksGalleryScreen = (): ReactNode => (
  <div data-testid="marks-gallery" className="flex flex-col gap-6 p-4">
    <h1 className="text-h1 text-ink">Team mark gallery</h1>
    <p className="text-body text-ink-secondary">
      Every team abbreviation mark, at two, three and four characters. This page
      exists so a browser can measure them. It is not linked from any
      navigation.
    </p>
    <div className="flex flex-col gap-2">
      <h2 className="text-h2 text-ink">Score rows</h2>
      {MARK_EVENTS.map((event) => (
        <ScoreRow key={event.eventId} event={event} />
      ))}
    </div>
    <div className="flex flex-col gap-2">
      <h2 className="text-h2 text-ink">Game detail mark, small</h2>
      <div className="flex flex-wrap items-center gap-2">
        {MARK_ABBREVIATIONS.map((a) => (
          <TeamMark key={a} name={a} abbreviation={a} size="sm" />
        ))}
      </div>
    </div>
    <div className="flex flex-col gap-2">
      <h2 className="text-h2 text-ink">Game detail mark, medium</h2>
      <div className="flex flex-wrap items-center gap-2">
        {MARK_ABBREVIATIONS.map((a) => (
          <TeamMark key={a} name={a} abbreviation={a} size="md" />
        ))}
      </div>
    </div>
    <div className="flex flex-col gap-2">
      <h2 className="text-h2 text-ink">Team profile mark, large</h2>
      <div className="flex flex-wrap items-center gap-2">
        {MARK_ABBREVIATIONS.map((a) => (
          <TeamMark key={a} name={a} abbreviation={a} size="lg" />
        ))}
      </div>
    </div>
    <div className="flex flex-col gap-2">
      <h2 className="text-h2 text-ink">Drawn marks, all three sizes</h2>
      <p className="text-body text-ink-secondary">
        The image branch. The last score row above carries the same URL on its
        away side, so the drawn mark and the lettered plate sit one under the
        other at the row size.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {(["sm", "md", "lg"] as const).map((size) => (
          <TeamMark
            key={size}
            name="Drawn Mark"
            abbreviation="DRW"
            logoUrl={SAMPLE_MARK_URL}
            size={size}
          />
        ))}
      </div>
    </div>
    <div className="flex flex-col gap-2">
      <h2 className="text-h2 text-ink">Favorite team plates</h2>
      <div className="flex flex-wrap items-center gap-2">
        {MARK_TEAM_IDS.map((id) => (
          <FanTeamPlate key={id} teamId={id} />
        ))}
      </div>
    </div>
  </div>
);

export const Route = createFileRoute("/gallery_/marks")({
  beforeLoad: requireSignedIn,
  component: MarksGalleryScreen,
});
